import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Chip, Divider, Paper, Typography } from '@mui/material';
import React from 'react';
import { Activity, DayOfWeek, Period } from '../../types';

interface TimelineSlot {
  id: string;
  name: string;
  type: 'period' | 'activity';
  startTime: string;
  endTime: string;
  duration: number;
  color: string;
  activityType?: Activity['type'];
}

interface PeriodTimelineProps {
  periods: Period[];
  activities: Activity[];
  selectedDay?: DayOfWeek;
  showDuration?: boolean;
  interactive?: boolean;
  onSlotClick?: (slot: TimelineSlot) => void;
}

export const PeriodTimeline: React.FC<PeriodTimelineProps> = ({
  periods,
  activities,
  selectedDay = 'monday',
  showDuration = true,
  interactive = false,
  onSlotClick,
}) => {
  // Generate timeline slots for the selected day
  const generateTimelineSlots = (): TimelineSlot[] => {
    const _slots: TimelineSlot[] = [];
    const dayActivities = activities.filter((a) => a.days?.includes(selectedDay));

    // Combine periods and activities and sort by start time
    const allSlots: TimelineSlot[] = [
      ...periods.map((p) => ({
        id: p.id,
        name: `Period ${p.periodNumber}`,
        type: 'period' as const,
        startTime: p.startTime,
        endTime: p.endTime,
        duration: p.duration,
        color: '#c8e6c9',
      })),
      ...dayActivities.map((a) => ({
        id: a.id,
        name: a.name,
        type: 'activity' as const,
        startTime: a.startTime,
        endTime: a.endTime,
        duration: a.duration,
        color: getActivityColor(a.type),
        activityType: a.type,
      })),
    ];

    return allSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const getActivityColor = (type: Activity['type']): string => {
    switch (type) {
      case 'registration':
        return '#e3f2fd';
      case 'assembly':
        return '#fff3e0';
      case 'break':
        return '#f3e5f5';
      case 'lunch':
        return '#ffe082';
      default:
        return '#f5f5f5';
    }
  };

  const getActivityIcon = (type?: Activity['type']): string => {
    switch (type) {
      case 'registration':
        return '📝';
      case 'assembly':
        return '👥';
      case 'break':
        return '☕';
      case 'lunch':
        return '🍽️';
      default:
        return '📚';
    }
  };

  const timelineSlots = generateTimelineSlots();

  // Calculate total time span for positioning
  const startTimes = timelineSlots.map((s) => s.startTime);
  const endTimes = timelineSlots.map((s) => s.endTime);
  const earliestTime = startTimes.reduce((min, time) => (time < min ? time : min), startTimes[0]);
  const latestTime = endTimes.reduce((max, time) => (time > max ? time : max), endTimes[0]);

  const parseTime = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const totalMinutes = parseTime(latestTime) - parseTime(earliestTime);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccessTimeIcon color="primary" />
        <Typography variant="h6">
          School Day Timeline - {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          position: 'relative',
        }}
      >
        {timelineSlots.map((slot, index) => {
          const _slotStart = parseTime(slot.startTime) - parseTime(earliestTime);
          const _widthPercent = (slot.duration / totalMinutes) * 100;

          return (
            <Box key={slot.id}>
              <Box
                onClick={() => interactive && onSlotClick?.(slot)}
                sx={{
                  p: 2,
                  backgroundColor: slot.color,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: interactive ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': interactive
                    ? {
                        boxShadow: 2,
                        transform: 'scale(1.01)',
                      }
                    : {},
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" component="span">
                    {slot.type === 'activity' ? getActivityIcon(slot.activityType) : '📚'}
                  </Typography>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {slot.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {slot.startTime} - {slot.endTime}
                      </Typography>
                      {showDuration && <Chip label={`${slot.duration} min`} size="small" variant="outlined" />}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Chip
                    label={slot.type === 'period' ? 'Lesson' : slot.activityType}
                    size="small"
                    color={slot.type === 'period' ? 'success' : 'default'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>

              {index < timelineSlots.length - 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 0.5,
                  }}
                >
                  <Divider sx={{ width: '100%' }} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Total teaching time: {periods.reduce((total, p) => total + p.duration, 0)} minutes (
          {Math.round((periods.reduce((total, p) => total + p.duration, 0) / 60) * 10) / 10} hours)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Number of periods: {periods.length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          School hours: {earliestTime} - {latestTime}
        </Typography>
      </Box>
    </Paper>
  );
};

export default PeriodTimeline;
